import React, { useState, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import { useSelector } from 'react-redux';
import Loader from '../../components/Loader';
import { RootState } from '../../redux/store';
import { getSchoolClassesConfig } from '../../apis/schoolConfig';
import { academicYears } from '../../constants/academicYears';
import DropdownSelect from '../../components/DropdownSelect';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import studentPromotionApis from '../../apis/studentPromotion';
import { ButtonContainer } from '../Exams/styled';
import { PageWrapper, Title } from '../common/styled';
import SelectAll from '../../components/SelectAllField/SelectAllField';
import { usersWithFinalPromotionAccess } from '../../constants/superAdminUsers';

const FinalPromotionPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.profile);
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [academicYear, setAcademicYear] = useState('2026-2027');
  const [toast, setToast] = useState<{
    message: string;
    severity: SnackbarSeverityType;
  } | null>(null);
  const [gradeOptions, setGradeOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const hasAccessToFinalPromotion = usersWithFinalPromotionAccess.includes(
    user.email || ''
  );

  const onSubmit = async () => {
    setSubmitting(true);
    setToast(null);
    try {
      await studentPromotionApis.promoteStudents({
        school: user.school,
        academicYear,
        grades: selectedGrades
      });
      setToast({
        message: 'Final Promotion successful',
        severity: SnackbarSeverityType.success
      });
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      setToast({
        message: `Something went wrong. Error: ${
          e?.errors[0]?.message || e?.message || ''
        }`,
        severity: SnackbarSeverityType.error
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    getSchoolClassesConfig(user.school)
      .then((res) => {
        const apiGrades =
          (res.data as any)?.grades?.filter(
            (currentGrade: string) =>
              !currentGrade?.toString().includes('-INCOMING')
          ) || [];
        setGradeOptions(
          apiGrades.map((grade: string) => ({ label: grade, value: grade }))
        );
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load classes config', e);
      });
  }, [user.school]);

  return (
    <>
      <Title>Final Promotion</Title>
      <PageWrapper>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            width: '100%',
            alignItems: 'flex-end'
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DropdownSelect
              label='Promoting AY'
              name='academicYear'
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value as string)}
              options={academicYears.map((y) => ({
                label: y.label,
                value: y.value
              }))}
              disabled={!hasAccessToFinalPromotion || submitting}
            />
            <Box sx={{ width: '200px' }}>
              <SelectAll
                label='Promoting to Grade'
                placeholder='Select Promoting Grade'
                required
                options={gradeOptions}
                selected={selectedGrades}
                onChange={setSelectedGrades}
                disabled={!hasAccessToFinalPromotion || submitting}
              />
            </Box>
          </Box>
          <ButtonContainer>
            <Button
              disabled={
                !hasAccessToFinalPromotion ||
                submitting ||
                selectedGrades.length === 0 ||
                !academicYear
              }
              onClick={onSubmit}
              variant='contained'
            >
              {submitting ? <Loader show /> : 'Promote'}
            </Button>
          </ButtonContainer>
        </Box>
        <ToastMessage
          open={!!toast}
          message={toast?.message || ''}
          severity={toast?.severity || SnackbarSeverityType.info}
          onClose={() => setToast(null)}
          autoHideDuration={5000}
        />
      </PageWrapper>
    </>
  );
};

export default FinalPromotionPage;
